'use client'

import { useState, useEffect } from 'react'
import { Loader2, PlusCircle } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

import { apiCreateSubscription, apiUpdateSubscription } from '@/lib/api/subscriptions'
import type { RecurringFrequency, SubscriptionItem } from '@/types/subscription'

const FREQUENCIES: RecurringFrequency[] = ['day', 'week', 'semi_month', 'month', 'year']

type Props = {
  open: boolean
  onOpenChange: (v: boolean) => void
  onCreated: (subscription: SubscriptionItem) => void
  mode?: 'create' | 'edit'
  subscription?: SubscriptionItem | null
}

export function CreateSubscriptionModal({
  open,
  onOpenChange,
  onCreated,
  mode = 'create',
  subscription = null,
}: Props) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [frequency, setFrequency] = useState<RecurringFrequency>('month')
  const [duration, setDuration] = useState(0)
  const [cycle, setCycle] = useState(1)

  const [trialStatus, setTrialStatus] = useState(false)
  const [trialPrice, setTrialPrice] = useState('0')
  const [trialFrequency, setTrialFrequency] = useState<RecurringFrequency>('month')
  const [trialDuration, setTrialDuration] = useState(0)
  const [trialCycle, setTrialCycle] = useState(1)

  const [status, setStatus] = useState(true)
  const [sortOrder, setSortOrder] = useState(0)

  const [introEnabled, setIntroEnabled] = useState(false)
  const [introPrice, setIntroPrice] = useState('')
  const [introDurationMonths, setIntroDurationMonths] = useState(3)

  async function submit() {
    try {
      setSaving(true)
      setError(null)

      if (introEnabled) {
        const normalPrice = Number(price || 0)
        const intro = Number(introPrice || 0)

        if (!intro || intro <= 0) {
          throw new Error('Agrega un precio introductorio válido')
        }

        if (!introDurationMonths || introDurationMonths <= 0) {
          throw new Error('Agrega la duración del precio introductorio')
        }

        if (normalPrice > 0 && intro >= normalPrice) {
          throw new Error('El precio introductorio debe ser menor al precio normal')
        }
      }

      const payload = {
        name,
        price,
        frequency,
        duration,
        cycle,
        trial_status: trialStatus,
        trial_price: trialPrice,
        trial_frequency: trialFrequency,
        trial_duration: trialDuration,
        trial_cycle: trialCycle,
        intro_enabled: introEnabled ? 1 : 0,
        intro_price: introEnabled ? introPrice : null,
        intro_duration_months: introEnabled ? introDurationMonths : null,
        status,
        sort_order: sortOrder,
      }

      const json =
        mode === 'edit' && subscription?.recurring_id
          ? await apiUpdateSubscription(subscription.recurring_id, payload)
          : await apiCreateSubscription(payload)

      if (json?.subscription) {
        onCreated(json.subscription)
        onOpenChange(false)

        setName('')
        setPrice('')
        setFrequency('month')
        setDuration(0)
        setCycle(1)
        setTrialStatus(false)
        setTrialPrice('0')
        setTrialFrequency('month')
        setTrialDuration(0)
        setTrialCycle(1)
        setIntroEnabled(false)
        setIntroPrice('')
        setIntroDurationMonths(3)
        setStatus(true)
      }
    } catch (e: any) {
      setError(e?.message || 'No se pudo crear la suscripción')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (!open) return

    if (mode === 'edit' && subscription) {
      setName(subscription.name || '')
      setPrice(String(subscription.price || ''))
      setFrequency(subscription.frequency || 'month')
      setDuration(Number(subscription.duration || 0))
      setCycle(Number(subscription.cycle || 1))

      setTrialStatus(!!subscription.trial_status)
      setTrialPrice(String(subscription.trial_price || 0))
      setTrialFrequency(subscription.trial_frequency || 'month')
      setTrialDuration(Number(subscription.trial_duration || 0))
      setTrialCycle(Number(subscription.trial_cycle || 0))

      setIntroEnabled(!!subscription.intro_enabled)
      setIntroPrice(subscription.intro_price != null ? String(subscription.intro_price) : '')
      setIntroDurationMonths(Number(subscription.intro_duration_months || 3))

      setStatus(!!subscription.status)
      setSortOrder(Number(subscription.sort_order || 0))
    }
  }, [open, mode, subscription])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode === 'edit' ? 'Editar suscripción' : 'Crear suscripción'}
          </DialogTitle>
          <DialogDescription>
            Crea un plan recurrente que podrá dar acceso a tu comunidad.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label>Nombre</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Plan mensual" />
          </div>

          <div className="space-y-2">
            <Label>Precio</Label>
            <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="19.00" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Duración</Label>
              <Input
                type="number"
                min={0}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Cada</Label>
              <Input
                type="number"
                min={1}
                value={cycle}
                onChange={(e) => setCycle(Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Frecuencia</Label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {FREQUENCIES.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-xl border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Precio introductorio</div>
                <div className="text-sm text-muted-foreground">
                  Cobra un precio promocional durante los primeros meses y luego vuelve al precio normal.
                </div>
              </div>
              <Switch checked={introEnabled} onCheckedChange={setIntroEnabled} />
            </div>

            {introEnabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio introductorio</Label>
                  <Input
                    value={introPrice}
                    onChange={(e) => setIntroPrice(e.target.value)}
                    placeholder="25.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Duración en meses</Label>
                  <Input
                    type="number"
                    min={1}
                    value={introDurationMonths}
                    onChange={(e) => setIntroDurationMonths(Number(e.target.value))}
                  />
                </div>

                <div className="md:col-span-2 text-xs text-muted-foreground">
                  Ejemplo: {introPrice || '25.00'} por {introDurationMonths || 3} meses,
                  luego {price || '49.00'} cada periodo hasta cancelar.
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Periodo de prueba</div>
                <div className="text-sm text-muted-foreground">
                  Activa un trial para nuevos miembros.
                </div>
              </div>
              <Switch checked={trialStatus} onCheckedChange={setTrialStatus} />
            </div>

            {trialStatus && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Precio trial</Label>
                  <Input
                    value={trialPrice}
                    onChange={(e) => setTrialPrice(e.target.value)}
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Duración trial</Label>
                  <Input
                    type="number"
                    min={0}
                    value={trialDuration}
                    onChange={(e) => setTrialDuration(Number(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Cada</Label>
                  <Input
                    type="number"
                    min={1}
                    value={trialCycle}
                    onChange={(e) => setTrialCycle(Number(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Frecuencia trial</Label>
                  <select
                    value={trialFrequency}
                    onChange={(e) => setTrialFrequency(e.target.value as RecurringFrequency)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-xl border p-4">
            <div>
              <div className="font-medium">Activo</div>
              <div className="text-sm text-muted-foreground">
                Solo los planes activos deben mostrarse a tus usuarios.
              </div>
            </div>
            <Switch checked={status} onCheckedChange={setStatus} />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={submit} disabled={saving} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlusCircle className="h-4 w-4" />}
              {saving ? 'Guardando...' : mode === 'edit' ? 'Guardar cambios' : 'Crear suscripción'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}